import { API_LABEL, DRIFT_FIELDS, type ChangeLogEntry, type Partner, type Tier } from "./model";
import { BAND_BY_STATUS, type HealthBand, type PartnerStatus } from "./partnerStatus";
import type {
  Growth,
  MetricsPortfolio,
  Overage,
  PartnerMetricsRow,
  Utilization,
} from "./metrics.types";

/**
 * Pure metric math for the Metrics Dashboard — a typed port of the POC's
 * `computeUtilization` / `computeGrowthN` / `classifyPartner` / … helpers.
 * No React, no globals, no data-source access: give it a portfolio, get rows
 * back. All of the interesting branching lives here so it can be unit-tested
 * directly (see `partnerMetrics.test.ts`), the way `auth-access.ts` is.
 * Display formatting (currency, overage label) lives in `metricsFormat.ts`.
 */

/** Default lookback for growth: compare the last 7 days against the 7 before. */
export const DEFAULT_GROWTH_WINDOW_DAYS = 7;

export function computeUtilization(partner: Partner): Utilization {
  const { saved, adhoc } = partner.usage;
  const savedPct = saved.cap ? Math.round((saved.used / saved.cap) * 100) : 0;
  const adhocPct = adhoc.cap ? Math.round((adhoc.used / adhoc.cap) * 100) : 0;
  return { savedPct, adhocPct, maxPct: Math.max(savedPct, adhocPct) };
}

/**
 * N-day growth: the most recent `windowDays` of daily call volume against the
 * `windowDays` immediately before it. Needs `2 * windowDays` points; returns a
 * "—" label otherwise.
 */
export function computeGrowth(
  series: number[],
  windowDays: number = DEFAULT_GROWTH_WINDOW_DAYS
): Growth {
  if (series.length < windowDays * 2) {
    return { pct: 0, label: "—", insufficientData: true };
  }
  const prior = series.slice(series.length - 2 * windowDays, series.length - windowDays);
  const recent = series.slice(series.length - windowDays);
  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
  const priorSum = sum(prior);
  const recentSum = sum(recent);
  if (priorSum === 0) {
    return recentSum > 0
      ? { pct: 100, label: "+100%", insufficientData: false }
      : { pct: 0, label: "0%", insufficientData: false };
  }
  const pct = Math.round(((recentSum - priorSum) / priorSum) * 100);
  return { pct, label: `${pct > 0 ? "+" : ""}${pct}%`, insufficientData: false };
}

/** Human-readable API labels the partner holds. Saved + Ad Hoc always; Firehose when enabled. */
export function apiBreadth(partner: Partner): string[] {
  const apis = [API_LABEL.saved, API_LABEL.adhoc];
  if (partner.usage.firehoseEnabled) apis.push(API_LABEL.firehose);
  return apis;
}

/** Count of change-log entries against this partner that touch a drift-sensitive field. */
export function manualDriftCount(partner: Partner, changeLog: ChangeLogEntry[]): number {
  return changeLog.filter(
    (entry) => entry.partner === partner.name && DRIFT_FIELDS.includes(entry.field)
  ).length;
}

/** Error responses as a percentage of recent activity (0 when there is none). */
export function errorRatePct(partner: Partner): number {
  const rows = partner.usage.recentActivity;
  if (rows.length === 0) return 0;
  return Math.round((rows.filter((r) => r.status === "Error").length / rows.length) * 100);
}

/**
 * Contract breach / revenue-leakage: the partner has exceeded a numeric cap
 * (used > cap), not merely approached it.
 */
export function computeOverage(partner: Partner): Overage {
  const { saved, adhoc } = partner.usage;
  const savedOver = Math.max(0, saved.used - saved.cap);
  const adhocOver = Math.max(0, adhoc.used - adhoc.cap);
  return {
    savedOver,
    adhocOver,
    total: savedOver + adhocOver,
    isBreached: savedOver + adhocOver > 0,
  };
}

/** The status ladder, worst first. A partner gets the first one it qualifies for. */
export function classifyPartner(input: {
  utilization: Utilization;
  growth: Growth;
  apiBreadth: string[];
  driftCount: number;
  overage: Overage;
}): { status: PartnerStatus; band: HealthBand } {
  const { utilization, growth, apiBreadth: breadth, driftCount, overage } = input;
  let status: PartnerStatus;
  if (overage.isBreached) status = "Contract Breach";
  else if (utilization.maxPct >= 100) status = "Needs Upgrade Now";
  else if (utilization.maxPct >= 75 && growth.pct >= 0) status = "Upsell Ready";
  else if (driftCount > 0) status = "Entitlement Review";
  else if (utilization.maxPct < 40 && breadth.length <= 2) status = "Under-adopted";
  else status = "Healthy";
  return { status, band: BAND_BY_STATUS[status] };
}

function tierFor(partner: Partner, tiers: Tier[]): Tier {
  const tier = tiers.find((t) => t.id === partner.tierId);
  if (!tier) {
    throw new Error(`No tier "${partner.tierId}" for partner "${partner.id}"`);
  }
  return tier;
}

/** Compute one row. Exported for focused tests; most callers want `computePartnerMetrics`. */
export function computePartnerRow(
  partner: Partner,
  tiers: Tier[],
  changeLog: ChangeLogEntry[],
  growthWindowDays: number = DEFAULT_GROWTH_WINDOW_DAYS
): PartnerMetricsRow {
  const utilization = computeUtilization(partner);
  const growth = computeGrowth(partner.usage.callVolumeSeries, growthWindowDays);
  const breadth = apiBreadth(partner);
  const driftCount = manualDriftCount(partner, changeLog);
  const overage = computeOverage(partner);
  const { status, band } = classifyPartner({
    utilization,
    growth,
    apiBreadth: breadth,
    driftCount,
    overage,
  });
  return {
    id: partner.id,
    partner,
    tier: tierFor(partner, tiers),
    utilization,
    growth,
    apiBreadth: breadth,
    driftCount,
    overage,
    savedUsage: partner.usage.saved,
    adhocUsage: partner.usage.adhoc,
    mediaPlayerViews30d: partner.usage.mediaPlayerViews30d,
    errorPct: errorRatePct(partner),
    status,
    band,
  };
}

/** Compute every partner row for a portfolio. The dashboard's entry point. */
export function computePartnerMetrics(
  portfolio: MetricsPortfolio,
  growthWindowDays: number = DEFAULT_GROWTH_WINDOW_DAYS
): PartnerMetricsRow[] {
  return portfolio.partners.map((partner) =>
    computePartnerRow(partner, portfolio.tiers, portfolio.changeLog, growthWindowDays)
  );
}
